/**
 * A source's converted text, editable while it waits for review and after indexing
 * (then a re-index carries the edit into search). #805, specs/dataset-workspace.md
 */
import React, { useState } from 'react';
import { Pencil } from 'lucide-react';
import { TextArea, Label, Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { updateSource } from './datasetApi';
import { Preview, Meta } from './styles';

const EDITABLE = new Set(['pending_review', 'needs_review', 'indexed', 'failed']);

export default function ConvertedText({ datasetId, source, markdown, canEdit: mayEdit, onSaved }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const canEdit = mayEdit && !!markdown && EDITABLE.has(source.status);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateSource(datasetId, source.id, { converted_markdown: text });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Label as="span">
        {t('datasetWorkspace.preview')}
        {source.metadata?.markdownEdited && <span style={{ color: '#888', fontWeight: 400 }}> · {t('datasetWorkspace.text.edited')}</span>}
      </Label>
      {editing ? (
        <>
          <TextArea value={text} onChange={(e) => setText(e.target.value)}
            style={{ minHeight: 320, fontFamily: 'monospace', fontSize: 12 }} />
          {source.status === 'indexed' && <Meta>{t('datasetWorkspace.rerun.editIndexedHint')}</Meta>}
          <Actions>
            <Button $size="small" $variant="primary" disabled={busy || !text.trim()} onClick={save}>{t('datasetWorkspace.save')}</Button>
            <Button $size="small" $variant="ghost" onClick={() => setEditing(false)}>{t('datasetWorkspace.cancel')}</Button>
            {error && <StatusLine $error>{error}</StatusLine>}
          </Actions>
        </>
      ) : (
        <>
          <Preview>{markdown || t('datasetWorkspace.noPreview')}</Preview>
          {canEdit && (
            <Actions>
              <Button $size="small" $variant="ghost" onClick={() => { setText(markdown); setEditing(true); }}>
                <Pencil /> {t('datasetWorkspace.text.edit')}
              </Button>
            </Actions>
          )}
        </>
      )}
    </div>
  );
}
